import { secondaryText } from "../../colors";
import * as React from "react";
import Popover from "components/Popover/Popover";
import FilterSearchBox from "components/FilterSearchBox";
import { List, ListItem } from "material-ui/List";
import { repository } from "clientInstance";
import { keyBy, sortBy } from "lodash";
import styles = require("./projectSwitcher.less");
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import { Link } from "react-router-dom";
import routeLinks from "../../routeLinks";
import { ProjectResource, ProjectGroupResource, Permission } from "client/resources";
import PermissionCheck, { isAllowed } from "components/PermissionCheck/PermissionCheck";
import { List as VirtualList, AutoSizer } from "react-virtualized";
import FocusListItem from "components/FocusListItem/FocustListItem";
import InternalLink from "../Navigation/InternalLink/InternalLink";

interface Project {
    id: string;
    slug: string;
    name: string;
    group: string;
}

interface ProjectSwitcherProps {
    open: boolean;
    onRequestClose: () => void;
    anchorEl: any;
}

interface ProjectSwitcherState {
    filter: string;
    projects: Project[];
    filteredList: Project[];
    showGroup: boolean;
    firstVisibleRow: number;
    focusedRow: number;
}

export default class ProjectSwitcher extends BaseComponent<ProjectSwitcherProps, ProjectSwitcherState> {
    searchRef: any;

    constructor(props: ProjectSwitcherProps) {
        super(props);

        this.refreshProjects = this.refreshProjects.bind(this);

        this.state = {
            filter: "",
            projects: [],
            filteredList: [],
            showGroup: false,
            firstVisibleRow: 0,
            focusedRow: -1
        };
    }

    async componentDidMount() {
        await this.refreshProjects();
    }

    componentWillReceiveProps(nextProps: ProjectSwitcherProps) {
        if (nextProps.open) {
            this.refreshProjects();
        }
    }

    render() {
        const list = this.state.filteredList;

        const virtualList = () => {
            const rowHeight = ({ index }: { index: number }) => {
                let height = this.state.showGroup ? 72 : 48;

                const p = list[index];
                if (p.name.length > 43) {
                    height += 18;
                }

                return height;
            };

            const renderRow = ({ key, index, isVisible, style }: any) => {
                const p = list[index];

                return <FocusListItem
                    key={key}
                    isFocused={index === this.state.focusedRow}
                    style={style}
                    containerElement={<InternalLink to={routeLinks.project(p.slug).root} />}
                    primaryText={p.name}
                    secondaryText={p.group}
                    onRequestClose={this.onRequestClose} />;
            };

            return <AutoSizer>
                {({ height, width }) =>
                    <div onKeyDown={this.onListKeyDown}>
                        <VirtualList
                            tabIndex={-1}
                            height={height}
                            rowCount={list.length}
                            estimatedRowSize={this.state.showGroup ? 72 : 48}
                            rowHeight={rowHeight}
                            onRowsRendered={this.onRowsRendered}
                            rowRenderer={({ index, key, isVisible, style }) =>
                                renderRow({
                                    index,
                                    key,
                                    isVisible,
                                    style
                                })}
                            width={width}
                        />
                    </div>}
            </AutoSizer>;
        };

        return <Popover
            open={this.props.open}
            anchorEl={this.props.anchorEl}
            onRequestClose={this.onRequestClose}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            targetOrigin={{ horizontal: "left", vertical: "top" }}>
            <div className={styles.container} onKeyDown={this.onKeyEsc}>
                {this.state.projects.length === 0 && <div className={styles.empty}>
                    There are no projects yet!
                </div>}
                {this.state.projects.length > 0 && <div className={styles.filter}>
                    <FilterSearchBox
                        innerRef={this.setSearchRef}
                        autoFocus={true}
                        value={this.state.filter}
                        hintText="Jump to project..."
                        onChange={this.handleFilterChanged}
                        fullWidth={true}
                        onKeyDown={this.onArrowDown}
                        containerClassName={styles.filterFieldContainer}
                    />
                </div>}
                <div className={styles.menuContainer}>
                    {virtualList()}
                </div>
            </div>
        </Popover>;
    }

    private onRowsRendered = ({ startIndex }: { startIndex: number }) => {
        this.setState({ firstVisibleRow: startIndex });
    }

    private setSearchRef = (el: any) => {
        this.searchRef = el;
    }

    private onRequestClose = () => {
        this.setState({
            filter: "",
            focusedRow: -1
        });
        this.props.onRequestClose();
    }

    private filteredList(projects: Project[], filter: string) {
        const matchesFilter = (n: string) => n.toLowerCase().search(filter.toLowerCase()) !== -1;

        return (filter.length > 0
            ? projects.filter(p => matchesFilter(p.name) || (p.group ? matchesFilter(p.group) : false))
            : projects);
    }

    private async refreshProjects() {
        const projectResources = await repository.Projects.all();
        let projectGroupMap: { [id: string]: ProjectGroupResource } = null;
        let showGroup = false;
        if (isAllowed({ permission: Permission.ProjectGroupView, projectGroup: "*" })) {
            const groups = await repository.ProjectGroups.all();

            if (groups.length > 1) {
                showGroup = true;
                projectGroupMap = keyBy(groups, "Id");
            }
        }

        const sortedProjects = sortBy(projectResources, p => p.Name.toLowerCase());
        const projectGroup = (gId: string) => {
            return projectGroupMap.hasOwnProperty(gId) ? projectGroupMap[gId].Name : null;
        };

        const projects = sortedProjects.map(p => ({
            id: p.Id,
            slug: p.Slug,
            name: p.Name,
            group: projectGroupMap ? projectGroup(p.ProjectGroupId) : null
        }));

        this.setState({ projects, filteredList: this.filteredList(projects, this.state.filter), showGroup });
    }

    private onKeyEsc = (event: any) => {
        if (event.key === "Escape") {
            this.onRequestClose();
        }
    }

    private onArrowDown = (event: KeyboardEvent) => {
        if (event.key === "ArrowDown" || event.key === "Tab") {
            if (this.state.filteredList.length === 0) {
                return;
            }

            this.setState({ focusedRow: this.state.firstVisibleRow });
            event.preventDefault();
        }
    }

    private onListKeyDown = (event: any) => {
        if (event.key === "ArrowDown" || (!event.shiftKey && event.key === "Tab")) {
            const lastIndex = this.state.filteredList.length - 1;
            this.setState((prevState) => {
                return { focusedRow: prevState.focusedRow <= lastIndex ? prevState.focusedRow + 1 : lastIndex };
            });
            event.preventDefault();
        }

        if (event.key === "ArrowUp" || (event.shiftKey && event.key === "Tab")) {

            if (this.state.focusedRow === 0) {
                this.searchRef.focus();
                this.setState({focusedRow: -1});
            } else {
                this.setState((prevState) => {
                    return { focusedRow: prevState.focusedRow > 0 ? prevState.focusedRow - 1 : 0 };
                });
            }
            event.preventDefault();
        }
    }

    private handleFilterChanged = (val: string) => {
        this.setState({
            filteredList: this.filteredList(this.state.projects, val),
            filter: val
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Navbar/ProjectSwitcher.tsx